import React from "react";
import {
  Search,
  X,
  Loader2,
  CheckCircle,
  ArrowUpCircle,
  ArrowDownCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  FileSpreadsheet,
} from "lucide-react";
import { FilterState, HistoryStatusFilter, WalletHistory } from "../../../types/Wallet";

interface HistoryTabProps {
  histories: WalletHistory[];
  historyLoading: boolean;
  historiesPagination: any;
  historyFilters: FilterState;
  setHistoryFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  historyPage: number;
  setHistoryPage: React.Dispatch<React.SetStateAction<number>>;
  totalHistoryPages: number;
  historyTypeFilter: string;
  setHistoryTypeFilter: React.Dispatch<React.SetStateAction<string>>;
  historySortBy: string;
  historySortOrder: string;
  handleHistorySort: (sortBy: string) => void;
  exportHistoryToExcel: () => Promise<void>;
  isExportingHistory: boolean;
  setSelectedTransaction: React.Dispatch<React.SetStateAction<any>>;
  setShowStatusModal: React.Dispatch<React.SetStateAction<boolean>>;
  historyStartDate: string;
  setHistoryStartDate: React.Dispatch<React.SetStateAction<string>>;
  historyEndDate: string;
  setHistoryEndDate: React.Dispatch<React.SetStateAction<string>>;
  historyStatusFilter: HistoryStatusFilter;
  setHistoryStatusFilter: React.Dispatch<React.SetStateAction<HistoryStatusFilter>>;
}

export const HistoryTab: React.FC<HistoryTabProps> = ({
  histories,
  historyLoading,
  historiesPagination,
  historyFilters,
  setHistoryFilters,
  historyPage,
  setHistoryPage,
  totalHistoryPages,
  historyTypeFilter,
  setHistoryTypeFilter,
  historySortBy,
  historySortOrder,
  handleHistorySort,
  exportHistoryToExcel,
  isExportingHistory,
  setSelectedTransaction,
  setShowStatusModal,
  historyStartDate,
  setHistoryStartDate,
  historyEndDate,
  setHistoryEndDate,
  historyStatusFilter,
  setHistoryStatusFilter,
}) => {
  const currentHistories = histories || [];

  const isFilterActive = historyStartDate !== "" || historyEndDate !== "" || historyStatusFilter !== "";

  const handleClearFilters = () => {
    setHistoryStartDate("");
    setHistoryEndDate("");
    setHistoryStatusFilter("");
    setHistoryPage(1);
  };

  return (
    <div>
      {/* Search + Filter */}
      <div className="p-4 flex justify-between items-center border-b border-gray-100 flex-wrap gap-3">
        <div className="flex-1 min-w-[200px] max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher dans l'historique..."
              value={historyFilters.search}
              onChange={(e) => {
                setHistoryFilters({ search: e.target.value });
                setHistoryPage(1);
              }}
              className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="date"
            value={historyStartDate}
            onChange={(e) => {
              setHistoryStartDate(e.target.value);
              setHistoryPage(1);
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 text-sm"
            placeholder="Du"
          />
          <span className="text-gray-400 text-sm">→</span>
          <input
            type="date"
            value={historyEndDate}
            onChange={(e) => {
              setHistoryEndDate(e.target.value);
              setHistoryPage(1);
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 text-sm"
            placeholder="Au"
          />

          <select
            value={historyTypeFilter}
            onChange={(e) => {
              setHistoryTypeFilter(e.target.value);
              setHistoryPage(1);
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 text-sm"
          >
            <option value="">Tous les types</option>
            <option value="RECHARGE">Recharges</option>
            <option value="DEBIT">Débits</option>
          </select>

          <select
            value={historyStatusFilter}
            onChange={(e) => {
              setHistoryStatusFilter(e.target.value as HistoryStatusFilter);
              setHistoryPage(1);
            }}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700 text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="COMPLETED">Complété</option>
            <option value="PENDING">En attente</option>
            <option value="CANCELLED">Annulé</option>
          </select>

          {isFilterActive && (
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-1 text-sm"
              title="Effacer les filtres"
            >
              <X className="w-4 h-4" />
              Effacer
            </button>
          )}

          <button
            onClick={exportHistoryToExcel}
            disabled={isExportingHistory}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700 transition-colors flex items-center space-x-2 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            title="Exporter l'historique en Excel"
          >
            {isExportingHistory ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <FileSpreadsheet className="w-4 h-4" />
            )}
            <span>{isExportingHistory ? "Export..." : "Exporter Excel"}</span>
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Type</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Statut</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                <button
                  onClick={() => handleHistorySort("name")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Utilisateur
                  {historySortBy === "name" ? (
                    historySortOrder === "asc" ? (
                      <ArrowUp className="w-4 h-4" />
                    ) : (
                      <ArrowDown className="w-4 h-4" />
                    )
                  ) : (
                    <ArrowUpDown className="w-4 h-4 opacity-30" />
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Pays / Devise</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">Solde avant</th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">
                <button
                  onClick={() => handleHistorySort("amount")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors ml-auto"
                >
                  Montant
                  {historySortBy === "amount" ? (
                    historySortOrder === "asc" ? (
                      <ArrowUp className="w-4 h-4" />
                    ) : (
                      <ArrowDown className="w-4 h-4" />
                    )
                  ) : (
                    <ArrowUpDown className="w-4 h-4 opacity-30" />
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">Solde après</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Description</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">
                <button
                  onClick={() => handleHistorySort("created_at")}
                  className="flex items-center gap-1 hover:text-gray-900 transition-colors"
                >
                  Date
                  {historySortBy === "created_at" ? (
                    historySortOrder === "asc" ? (
                      <ArrowUp className="w-4 h-4" />
                    ) : (
                      <ArrowDown className="w-4 h-4" />
                    )
                  ) : (
                    <ArrowUpDown className="w-4 h-4 opacity-30" />
                  )}
                </button>
              </th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {historyLoading ? (
              <tr>
                <td colSpan={10} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                    <p className="text-gray-600 text-sm">Chargement de l'historique...</p>
                  </div>
                </td>
              </tr>
            ) : currentHistories.length > 0 ? (
              currentHistories.map((h) => (
                <tr key={h.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    {h.type === "RECHARGE" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <ArrowUpCircle className="w-3 h-3" /> Recharge
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        <ArrowDownCircle className="w-3 h-3" /> Débit
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {h.status === "COMPLETED" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                        <CheckCircle className="w-3 h-3" /> Complété
                      </span>
                    ) : h.status === "CANCELLED" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                        <X className="w-3 h-3" /> Annulé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                        <Loader2 className="w-3 h-3" /> En attente
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-800">
                        {h.user?.first_name} {h.user?.last_name}
                      </p>
                      <p className="text-xs text-gray-500">{h.user?.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-gray-800">{h.country?.name || "—"}</p>
                      <p className="text-xs text-gray-500">
                        {h.country?.code} · +{h.country?.phonecode} · {h.country?.devise}
                      </p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-500">
                    {h.account_before != null ? h.account_before.toLocaleString() : "—"}
                    <span className="text-xs text-gray-400 ml-1">{h.country?.devise}</span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className={`font-bold ${h.type === "RECHARGE" ? "text-green-600" : "text-red-600"}`}>
                      {h.type === "RECHARGE" ? "+" : "-"}
                      {h.amount.toLocaleString()}
                    </span>
                    <span className="text-xs text-gray-500 ml-1">{h.country?.devise}</span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm text-gray-500">
                    {h.account_after != null ? h.account_after.toLocaleString() : "—"}
                    <span className="text-xs text-gray-400 ml-1">{h.country?.devise}</span>
                  </td>
                  <td
                    className="px-6 py-4 text-sm text-gray-600 max-w-md whitespace-normal break-words"
                    title={h.description || ""}
                  >
                    {h.description || "—"}
                  </td>
                  <td className="px-6 py-4 text-xs text-gray-500">
                    {h.created_at ? new Date(h.created_at).toLocaleString("fr-FR") : "—"}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => {
                          setSelectedTransaction(h);
                          setShowStatusModal(true);
                        }}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-xs font-medium"
                        title="Changer le statut"
                      >
                        <RefreshCw className="w-3.5 h-3.5" />
                        Statut
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="px-6 py-8 text-center text-gray-500">
                  {isFilterActive
                    ? "Aucun historique trouvé pour ces filtres"
                    : historyFilters.search
                      ? "Aucun historique trouvé pour cette recherche"
                      : "Aucun historique trouvé"}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {historyLoading ? (
            <span className="text-gray-400">Chargement...</span>
          ) : (
            <span>
              Page {historyPage} sur {totalHistoryPages} ({historiesPagination?.total || 0} historiques au total)
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setHistoryPage(Math.max(1, historyPage - 1))}
            disabled={historyPage === 1 || historyLoading}
            className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-100"
          >
            Précédent
          </button>
          <button
            onClick={() => setHistoryPage(Math.min(totalHistoryPages, historyPage + 1))}
            disabled={historyPage === totalHistoryPages || historyLoading}
            className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 hover:bg-gray-100"
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
};
